import { AIChatbotEvents, trackAIChatbotEvent } from 'Analytics/ai-chatbot-events';
import Shepherd from 'shepherd.js';

import { lockScroll, unlockScroll } from 'Utils/dom';
import { isLandscape, isMobile } from 'Utils/viewport';

const VaiaProductTour = (compact = false) => {
    const _TOOLTIP_BACKDROP_TRANSITION_DURATION = 300;

    const addProgress = () => {
        const currentStep = tour?.getCurrentStep();
        const currentStepElement = currentStep?.getElement();
        const footer = currentStepElement?.querySelector('.shepherd-footer');
        const progress = document.createElement('span');

        progress.innerText = `${tour?.steps.indexOf(currentStep) + 1} of ${tour?.steps.length}`;
        progress.classList.add('shepherd-progress');

        footer?.insertAdjacentElement('afterbegin', progress);
    };

    // Resolved when the step shows - the suggestion lives in the trigger's shadow root
    const mobileTriggerAnchor = () => document.querySelector('vm-chat-mobile-trigger')?.firstSuggestion;

    const compactAttachTo = (() => {
        if (isMobile()) {
            return {
                element: mobileTriggerAnchor,
                on: 'bottom-start',
            };
        }

        return {
            element: '.js_vmChatbotInline .js_chatbotPlaceholderSuggestion:nth-child(1)',
            on: 'left',
        };
    })();

    const compactSteps = [
        {
            id: 'vaia-product-tour-step-compact-1',
            title: 'Click here to read the summary <i class="icon icon-chatbot"></i>',
            text: "Generate an AI summary and review what's discussed in this presentation.",
            attachTo: compactAttachTo,
            buttons: [],
            when: {
                show() {
                    trackAIChatbotEvent(AIChatbotEvents.TOOLTIP_APPEAR);
                },
            },
        },
    ];

    const desktopSteps = [
        {
            id: 'vaia-product-tour-step-1',
            title: 'Save time with Vumedi AI <i class="icon icon-chatbot"></i>',
            text: 'Ask for the main points in a presentation. Responses come from the video’s transcript.',
            attachTo: {
                element: '.js_vmChatbotInline .js_chatbotPlaceholderSuggestion:nth-child(3)',
                on: 'left-start',
            },
            buttons: [
                {
                    action() {
                        trackAIChatbotEvent(AIChatbotEvents.INFO_MODAL_CLICKED);
                        return this.next();
                    },
                    classes: 'vm-button vm-button--sm vm-button--secondary',
                    text: 'Next',
                },
            ],
            extraHighlights: ['.js_vmChatbotInline'],
            when: {
                show() {
                    trackAIChatbotEvent(AIChatbotEvents.INFO_MODAL_APPEAR);
                },
            },
        },
        {
            id: 'vaia-product-tour-step-2',
            title: 'Click the button to try it now <i class="icon icon-chatbot"></i>',
            text: 'Quickly generate an AI summary while you watch the video.',
            attachTo: {
                element: '.js_vmChatbotInline .js_chatbotPlaceholderSuggestion:nth-child(1)',
                on: 'left-start',
            },
            buttons: [
                {
                    action() {
                        return this.next();
                    },
                    classes: 'vm-button vm-button--sm vm-button--secondary vm-button--try-it-now',
                    text: 'Try it now',
                },
            ],
            when: {
                show() {
                    trackAIChatbotEvent(AIChatbotEvents.TOOLTIP_APPEAR);
                },
            },
        },
    ];

    const mobileSteps = [
        {
            id: 'vaia-product-tour-step-mobile-1',
            title: `<span>New Update</span><span>Save time with Vumedi AI</span>`,
            text: 'Ask for the main points in a presentation. Responses come from the video’s transcript.',
            buttons: [
                {
                    action() {
                        trackAIChatbotEvent(AIChatbotEvents.INFO_MODAL_CLICKED);
                        return this.next();
                    },
                    classes: 'vm-button vm-button--sm vm-button--primary',
                    text: 'Continue',
                },
            ],
            when: {
                show() {
                    trackAIChatbotEvent(AIChatbotEvents.INFO_MODAL_APPEAR);
                    window.scrollTo({ top: 0 });
                    setTimeout(() => {
                        lockScroll();
                    }, _TOOLTIP_BACKDROP_TRANSITION_DURATION);
                },
            },
        },
        {
            id: 'vaia-product-tour-step-mobile-2',
            title: 'Tap the button to try it now',
            text: 'Quickly generate an AI summary while you watch the video.',
            attachTo: {
                element: mobileTriggerAnchor,
                on: 'bottom-start',
            },
            buttons: [],
            when: {
                show() {
                    trackAIChatbotEvent(AIChatbotEvents.TOOLTIP_APPEAR);
                    window.scrollTo({ top: 0 });
                },
                hide() {
                    unlockScroll();
                },
            },
        },
    ];

    const tourClass = (() => {
        if (compact) {
            return 'vm-product-tour--compact';
        }

        if (isMobile()) {
            return 'vm-product-tour--mobile';
        }

        return 'vm-product-tour--desktop';
    })();

    const tour = new Shepherd.Tour({
        defaultStepOptions: {
            cancelIcon: {
                enabled: false,
            },
            classes: ['vm-product-tour', 'vm-product-tour--vaia', tourClass].join(' '),
            scrollTo: isMobile() ? { behavior: 'smooth', block: 'nearest' } : false,
            modalOverlayOpeningRadius: isMobile() ? 8 : 12,
            when: {
                show() {
                    if (tourClass === 'vm-product-tour--desktop') {
                        addProgress();
                    }
                },
            },
        },
        useModalOverlay: true,
    });

    const closeProductTour = (ev) => {
        let isQuestionClick = false;
        let iterator = ev.target;

        do {
            if (
                iterator.classList.contains('js_chatbotPlaceholderSuggestion') ||
                iterator.classList.contains('vm-button--chatbot-send')
            ) {
                isQuestionClick = true;
                break;
            }
        } while ((iterator = iterator?.parentElement));

        if (isQuestionClick) {
            tour.cancel();
        }
    };

    // Mobile suggestions are in a shadow root, so `closeProductTour` can't see them; the
    // trigger's select event is the equivalent signal
    const cancelOnMobileSelect = () => {
        tour.cancel();
    };

    const toggleCollapseButton = (display = false) => {
        const target = document.querySelector('.js_vmChatbotInline .js_chatbotCollapse');

        if (!target) {
            return;
        }

        if (display) {
            target.style.removeProperty('display');
        } else {
            target.style.setProperty('display', 'none');
        }
    };

    const cancelDueToLandscape = () => {
        tour.landscapeCancelled = true;
        unlockScroll();
        tour.cancel();
    };

    const handleResize = () => {
        if (isLandscape()) {
            cancelDueToLandscape();
        }
    };

    const steps = (() => {
        if (compact) {
            return compactSteps;
        }

        if (isMobile()) {
            return mobileSteps;
        }

        return desktopSteps;
    })();

    steps.forEach((step) => tour.addStep(step));

    tour.on('active', () => {
        document.body.addEventListener('click', closeProductTour);
        document.addEventListener('vm-chat-mobile-trigger:select', cancelOnMobileSelect);
        toggleCollapseButton();

        if (isMobile()) {
            if (isLandscape()) {
                cancelDueToLandscape();
                return;
            }
            window.addEventListener('resize', handleResize);
        }
    });

    const cleanupTour = () => {
        document.body.removeEventListener('click', closeProductTour);
        document.removeEventListener('vm-chat-mobile-trigger:select', cancelOnMobileSelect);
        toggleCollapseButton(true);
        unlockScroll();

        if (isMobile()) {
            window.removeEventListener('resize', handleResize);
        }
    };

    tour.on('cancel', cleanupTour);
    tour.on('complete', cleanupTour);

    return tour;
};

export { VaiaProductTour };
